import { useState } from "react";
import { AdvancedChart } from "./AdvancedChart";
import { OrderBook } from "./OrderBook";
import { TransactionHistoryPanel } from "./TransactionHistoryPanel";
import { FloatingTradeWidget } from "./FloatingTradeWidget";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, Info, Copy, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";

interface ProTerminalLayoutProps {
    coin: any; // Using any for now to avoid extensive type imports, but should use Database['coins']['Row']
}

export function ProTerminalLayout({ coin }: ProTerminalLayoutProps) {
    const [activeTab, setActiveTab] = useState("trades");
    const [imageLoaded, setImageLoaded] = useState(false);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: "Copied to clipboard" });
    };

    return (
        <div className="flex flex-col h-[calc(100vh-64px)] bg-black text-white relative overflow-hidden">
            {/* Top Bar: Token Info - Compact */}
            <header className="h-14 border-b border-white/5 flex items-center px-4 justify-between bg-black/50 backdrop-blur-md z-10 w-full shrink-0">
                <div className="flex items-center gap-4">
                    <div className="relative w-8 h-8 rounded-full ring-2 ring-primary/20 overflow-hidden">
                        {!imageLoaded && <Skeleton className="absolute inset-0" />}
                        <img
                            src={coin.image || "/placeholder.svg"}
                            className={cn("w-full h-full object-cover", !imageLoaded && "opacity-0")}
                            alt={coin.name}
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageLoaded(true)}
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-bold text-sm tracking-wide uppercase">{coin.ticker}</h1>
                            <span className="text-xs text-muted-foreground hidden sm:inline-block">/ SOL</span>
                            <Badge variant={coin.price_change_24h >= 0 ? "default" : "destructive"} className="h-5 px-1.5 text-[10px] bg-opacity-20 hover:bg-opacity-30">
                                {coin.price_change_24h >= 0 ? "+" : ""}{coin.price_change_24h?.toFixed(2)}%
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
                            <span className="cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(coin.mint_address || "")}>
                                CA: {coin.mint_address?.slice(0, 4)}...{coin.mint_address?.slice(-4)}
                                <Copy className="inline h-2.5 w-2.5 ml-1" />
                            </span>
                            <span>|</span>
                            <span>MC: ${(coin.market_cap || 0).toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs border-white/10 bg-white/5 hover:bg-white/10">
                        <Star className="h-3.5 w-3.5 mr-1" /> Watch
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </Button>
                </div>
            </header>

            {/* Main Grid Content */}
            <div className="flex-1 flex min-h-0">

                {/* LEFT: Chart (Flexible) */}
                <div className="flex-1 flex flex-col min-w-0 border-r border-white/5 relative group">
                    <AdvancedChart coinId={coin.id} className="flex-1 w-full h-full border-none rounded-none" />

                    {/* Overlay Toggle for Fullscreen could go here */}
                </div>

                {/* RIGHT: Data Panel (Fixed Width) */}
                <div className="w-[380px] flex flex-col bg-black/20 border-l border-white/5 shrink-0 hidden lg:flex">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                        <div className="flex items-center justify-between border-b border-white/5 px-2 bg-black/40">
                            <TabsList className="h-9 bg-transparent p-0">
                                <TabsTrigger value="book" className="text-xs px-3 h-9 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary rounded-none transition-none">Order Book</TabsTrigger>
                                <TabsTrigger value="trades" className="text-xs px-3 h-9 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary rounded-none transition-none">Recent Trades</TabsTrigger>
                            </TabsList>
                        </div>

                        <TabsContent value="book" className="flex-1 min-h-0 m-0 p-0">
                            <div className="h-full overflow-hidden">
                                <OrderBook coinId={coin.id} className="border-none rounded-none bg-transparent h-full" />
                            </div>
                        </TabsContent>

                        <TabsContent value="trades" className="flex-1 min-h-0 m-0 p-0 relative">
                            <ScrollArea className="h-full w-full">
                                <div className="p-2">
                                    {/* We reuse TransactionHistoryPanel but wrap it to strip card styles if possible or just accept them */}
                                    <TransactionHistoryPanel coinId={coin.id} className="border-none shadow-none bg-transparent" />
                                </div>
                            </ScrollArea>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Floating Widget - Positioned absolutely or fixed */}
            {/* For Pro Terminal, we might want it DOCKED at the bottom or floating. 
          Let's keep it floating for now but styled properly.
       */}
            <FloatingTradeWidget coin={coin} defaultPosition={{ x: window.innerWidth - 360, y: 80 }} />
        </div>
    );
}
